import { Constants } from '@constants';
import { numbersOnly } from '@forms/helpers/normalisers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconEuro, IconPound } from '@iwoca/icons';
import { Input, Slider } from '@iwoca/orion';
import { ErrorMessage, Text } from '@iwoca/orion-ui';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    selectedAmountGreaterThanMaxAvailableCash,
    selectedAmountLessThanMinDrawDown
} from './isSelectedAmountValid';

export const ChooseAmountInputWithSlider = ({
    label,
    description,
    selectedAmount,
    setSelectedAmount,
    maxAmount
}: {
    label: React.ReactNode;
    description?: React.ReactNode;
    selectedAmount: number | null;
    setSelectedAmount: (amount: number) => void;
    maxAmount: number;
}) => {
    const intl = useIntl();

    const minAmount = Constants.MIN_DRAW_DOWN_AMOUNT;

    const debounceSetSelectedAmount = _.debounce((value: string) => {
        const numericValue = parseInt(numbersOnly(value), 10);
        if (isNaN(numericValue)) {
            setSelectedAmount(0);
        } else {
            setSelectedAmount(numericValue ?? 0);
        }
    }, 100);

    const selectedAmountOrMaxAmount = selectedAmount ?? maxAmount;

    return (
        <div className="w-full space-y-xl">
            <div className="space-y-m">
                <div>
                    {label}
                    {description ? (
                        <Text className="text-s text-structure-40">
                            {description}
                        </Text>
                    ) : null}
                </div>
                <Input
                    type="text"
                    id="choose-amount"
                    iconLeft={
                        getCountry() === 'UK' ? <IconPound /> : <IconEuro />
                    }
                    min={minAmount}
                    max={maxAmount}
                    className="w-full"
                    value={intl.formatNumber(selectedAmountOrMaxAmount)}
                    onChange={(e) => debounceSetSelectedAmount(e.target.value)}
                />
                <SelectedAmountValidationError
                    selectedAmount={selectedAmountOrMaxAmount}
                    maxAvailableCash={maxAmount}
                />
            </div>

            <div className="w-full">
                <Slider
                    min={minAmount}
                    max={maxAmount}
                    defaultValue={maxAmount}
                    value={Number(selectedAmount)}
                    onChange={(e) => debounceSetSelectedAmount(e.target.value)}
                    step={10}
                />
                <div className="text-sm text-grey-600 mt-l flex w-full justify-between">
                    <span className="text-s text-structure-40">
                        <FormattedCurrency value={minAmount} />
                    </span>
                    <span className="text-s text-structure-40">
                        <FormattedCurrency value={maxAmount} />
                    </span>
                </div>
            </div>
        </div>
    );
};

const SelectedAmountValidationError = ({
    selectedAmount,
    maxAvailableCash
}: {
    selectedAmount: number;
    maxAvailableCash: number;
}) => {
    if (
        selectedAmountGreaterThanMaxAvailableCash(
            selectedAmount,
            maxAvailableCash
        )
    ) {
        return (
            <ErrorMessage>
                <ErrorMessage.Icon />
                <ErrorMessage.Text>
                    <FormattedMessage
                        id="_.apply_flexi_loan.requestedAmount.validation.tooHigh"
                        defaultMessage="Please enter an amount less than {maxAmount, number, :: currency/GBP precision-integer}"
                        values={{
                            maxAmount: maxAvailableCash
                        }}
                    />
                </ErrorMessage.Text>
            </ErrorMessage>
        );
    }

    if (
        selectedAmount === 0 ||
        selectedAmountLessThanMinDrawDown(selectedAmount)
    ) {
        return (
            <ErrorMessage>
                <ErrorMessage.Icon />
                <ErrorMessage.Text>
                    <FormattedMessage
                        id="_.apply_flexi_loan.requestedAmount.validation.tooLow"
                        defaultMessage="Please enter an amount greater than {minAmount, number, :: currency/GBP precision-integer}"
                        values={{
                            minAmount: Constants.MIN_DRAW_DOWN_AMOUNT
                        }}
                    />
                </ErrorMessage.Text>
            </ErrorMessage>
        );
    }

    return null;
};
